import React from 'react';
import { motion } from 'framer-motion';
import { brandInfo, coreValues } from '../data/mock';
import { Music, Mic2, Radio, Heart, TrendingUp } from 'lucide-react';

const valueIcons = {
  Creativity: Music,
  Authenticity: Mic2,
  Excellence: Radio,
  Community: Heart,
  Growth: TrendingUp,
};

const AboutSection = () => {
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12,
      },
    },
  };

  return (
    <section id="about" className="py-24 lg:py-32 bg-black">
      <div className="max-w-7xl mx-auto px-6 lg:px-12">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-16"
        >
          <span className="service-tag" style={{ backgroundColor: '#ffd1e7', color: '#151515' }}>
            About Us
          </span>
          <h2 className="font-display text-4xl md:text-6xl lg:text-7xl font-extrabold uppercase text-white mt-4 mb-6">
            Bringing Deep House<br />
            <span className="text-light-pink">To Life</span>
          </h2>
        </motion.div>

        {/* Main Content Grid */}
        <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 items-center mb-20">
          {/* Image */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="relative"
          >
            <div className="relative rounded-lg overflow-hidden">
              <img
                src={brandInfo.djImage}
                alt={brandInfo.founder}
                className="w-full aspect-[4/5] object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
            </div>
            {/* Floating Card */}
            <div className="absolute -bottom-6 -right-6 bg-light-pink p-6 rounded-lg max-w-xs">
              <p className="font-mono text-xs uppercase tracking-widest text-black/60 mb-2">Founder</p>
              <p className="font-display text-2xl font-bold text-black uppercase">{brandInfo.founder}</p>
            </div>
          </motion.div>

          {/* Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <p className="text-white/80 text-lg lg:text-xl leading-relaxed mb-8">
              <span className="text-light-pink font-semibold">LoxitonDeepHouse</span> is a South African independent record label dedicated to nurturing, producing, and promoting the finest Deep House, Soulful House, and Afro House sounds.
            </p>
            <p className="text-white/60 text-base lg:text-lg leading-relaxed mb-8">
              Established by <span className="text-white">Morena TheDeeJay</span>, the label stands as a creative home for emerging and established artists who share a passion for innovative music and authentic storytelling through sound.
            </p>
            <p className="text-white/60 text-base lg:text-lg leading-relaxed mb-12">
              Driven by the belief that music can shape culture, inspire communities, and connect people, LoxitonDeepHouse focuses on delivering high-quality productions that resonate locally and globally.
            </p>

            {/* Vision & Mission */}
            <div className="grid sm:grid-cols-2 gap-6">
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="font-mono text-xs uppercase tracking-widest text-mid-purple mb-3">Vision</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  To become Africa's leading Deep House record label, recognized for artistic innovation, talent development, and global impact.
                </p>
              </div>
              <div className="bg-white/5 rounded-lg p-6 border border-white/10">
                <h3 className="font-mono text-xs uppercase tracking-widest text-mid-yellow mb-3">Mission</h3>
                <p className="text-white/80 text-sm leading-relaxed">
                  To discover and develop world-class musical talent and produce timeless Deep House records that captivate global audiences.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Core Values */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-2 md:grid-cols-5 gap-4"
        >
          {coreValues.map((value, index) => {
            const Icon = valueIcons[value.title];
            const colors = ['#ffd1e7', '#d987ff', '#88a2ff', '#f6fd87', '#78d692'];
            return (
              <motion.div
                key={value.id}
                variants={itemVariants}
                className="bg-white/5 rounded-lg p-6 border border-white/10 hover:border-white/20 transition-colors group"
              >
                <div
                  className="w-12 h-12 rounded-full flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                  style={{ backgroundColor: colors[index] }}
                >
                  <Icon className="w-6 h-6 text-black" />
                </div>
                <h4 className="font-display text-lg font-bold text-white uppercase mb-2">{value.title}</h4>
                <p className="text-white/60 text-sm leading-relaxed">{value.description}</p>
              </motion.div>
            );
          })}
        </motion.div>
      </div>
    </section>
  );
};

export default AboutSection;
